import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Input,
    output,
    input,
    ViewEncapsulation,
    model,
    effect,
    signal,
} from '@angular/core';
import {
    FiTimePickerHelperService,
    TimePickerSegmentIndex,
} from '@fi-shared/fi-time-picker-input/fi-time-picker-helper.service';
import {
    ITimeSegment,
    ITimePickerTimeLimits,
    ITimeSegmentInputData,
    IAvailableTimeSegments,
} from '../fi-time-picker-input.interfaces';
import { isDefined } from '@fi-shared/utils';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';

@Component({
    selector: 'fi-time-picker-segment-input',
    templateUrl: './fi-time-picker-segment-input.component.html',
    styleUrls: ['./fi-time-picker-segment-input.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'fi-time-picker-segment-input',
    },
})
export class FiTimePickerSegmentInputComponent {
    @Input() labelTextId: string;
    @Input() inputId: string;

    public value = model<ITimeSegmentInputData>();

    public isDisabled = input<boolean>(false);
    public showSeconds = input<boolean>(false);
    public segments = input.required<IAvailableTimeSegments>();
    public timeLimits = input.required<ITimePickerTimeLimits>();

    public isNextDayCapable = input<boolean>(false);
    public isNextDay = model<boolean>(false);

    public enterPressed = output<Event>();
    public focused = output<string>();
    public blurred = output<void>();

    private fiTimePickerHelperService = inject(FiTimePickerHelperService);

    protected inputValue: string | undefined = undefined;
    protected readonly TimeSegmentWrapperIdStartStr = 'time-segment-wrapper-';
    protected readonly TimeSegmentButtonClass = 'time-segment-button';

    protected inputMask = computed(() => this.fiTimePickerHelperService.getInputMask(this.showSeconds()));
    public isSelectedTimeSegmentInvalid = computed(() => {
        return (
            this.isTimeSegmentDisabled(this.value()?.hours, TimePickerSegmentIndex.Hours) ||
            this.isTimeSegmentDisabled(this.value()?.minutes, TimePickerSegmentIndex.Minutes) ||
            this.isTimeSegmentDisabled(this.value()?.seconds, TimePickerSegmentIndex.Seconds)
        );
    });
    public isDropDownVisible = signal<boolean>(false);

    constructor() {
        effect(() => {
            const value = this.value();
            this.setInputValue(value);
        });
    }

    protected displayDropDown(): void {
        this.isDropDownVisible.set(true);
        setTimeout(() => {
            this.scrollToFirstEnabledSegmentButton();
        });
    }

    private scrollToFirstEnabledSegmentButton(): void {
        const scrollContainer = [
            document.getElementById(this.TimeSegmentWrapperIdStartStr + TimePickerSegmentIndex.Hours),
            document.getElementById(this.TimeSegmentWrapperIdStartStr + TimePickerSegmentIndex.Minutes),
            document.getElementById(this.TimeSegmentWrapperIdStartStr + TimePickerSegmentIndex.Seconds),
        ].filter((container) => !isNullOrUndefined(container));

        scrollContainer.forEach((container) => {
            const enabledSegmentButton = container.querySelector<HTMLElement>(
                `.${this.TimeSegmentButtonClass}:not(.disabled)`,
            );

            if (!enabledSegmentButton) {
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const buttonRect = enabledSegmentButton.getBoundingClientRect();

            container.scrollTo({
                top: container.scrollTop + buttonRect.top - containerRect.top,
                behavior: 'smooth',
            });
        });
    }

    private setInputValue(value: ITimeSegmentInputData): void {
        this.inputValue = this.fiTimePickerHelperService.convertTimeSegmentInputDataToString(value, this.showSeconds());
    }

    protected onInputValueChange(inputValue: string | undefined): void {
        const newValue = this.fiTimePickerHelperService.convertStringToTimeSegmentInputData(
            inputValue,
            this.segments(),
            this.isNextDay(),
        );
        this.value.set(newValue);
    }

    protected onTimeSegmentSelect(timeSegment: ITimeSegment, segmentIndex: TimePickerSegmentIndex): void {
        if (segmentIndex === TimePickerSegmentIndex.Hours) {
            this.value.update((value) => ({
                ...value,
                hours: timeSegment,
            }));
            if (this.isNextDayCapable()) {
                this.isNextDay.set(timeSegment.id > 23);
            }
        } else if (segmentIndex === TimePickerSegmentIndex.Minutes) {
            this.value.update((value) => ({
                ...value,
                minutes: timeSegment,
            }));
            this.setFirstEnabledHour();
        } else if (segmentIndex === TimePickerSegmentIndex.Seconds) {
            this.value.update((value) => ({
                ...value,
                seconds: timeSegment,
            }));
        }

        this.setInputValue(this.value());
    }

    private setFirstEnabledHour(): void {
        const currentHour = this.value()?.hours;
        
        if (isDefined(currentHour?.value) && !this.isTimeSegmentDisabled(currentHour, TimePickerSegmentIndex.Hours)) {
            return;
        }
        const hours = this.getFirstEnabledSegment(TimePickerSegmentIndex.Hours);
        this.value.update((value) => ({
            ...value,
            hours: hours,
        }));
    }

    protected isTimeSegmentSelected(timeSegment: ITimeSegment, segmentIndex: TimePickerSegmentIndex): boolean {
        if (segmentIndex === TimePickerSegmentIndex.Hours) {
            return this.value()?.hours?.id === timeSegment.id;
        }
        if (segmentIndex === TimePickerSegmentIndex.Minutes) {
            return this.value()?.minutes?.id === timeSegment.id;
        }
        if (segmentIndex === TimePickerSegmentIndex.Seconds) {
            return this.value()?.seconds?.id === timeSegment.id;
        }
        return false;
    }

    protected isTimeSegmentDisabled(timeSegment: ITimeSegment, segmentIndex: TimePickerSegmentIndex): boolean {
        if (this.isNextDay() && segmentIndex === TimePickerSegmentIndex.Minutes) {
            const lastHourIndex = this.segments().hours.length - 1;
            if (this.value().hours.id === this.segments().hours[lastHourIndex].id) {
                return timeSegment.id !== 0;
            }
        }

        const timeLimits = this.timeLimits();

        if (!isDefined(timeLimits)) {
            return false;
        }

        return this.fiTimePickerHelperService.isTimeSegmentDisabled(
            timeLimits,
            segmentIndex,
            timeSegment,
            this.value(),
        );
    }

    protected onBlur(): void {
        if (this.value()?.hours.id !== undefined && this.value()?.minutes.id === undefined) {
            this.value.update((value) => ({
                ...value,
                minutes: this.getFirstEnabledSegment(TimePickerSegmentIndex.Minutes),
            }));
        } else if (this.value()?.minutes.id !== undefined && this.value()?.hours.id === undefined) {
            this.value.update((value) => ({
                ...value,
                hours: this.getFirstEnabledSegment(TimePickerSegmentIndex.Hours),
            }));
        }
        this.isDropDownVisible.set(false);
        this.blurred.emit();
    }

    private getSegmentByIndex(segmentIndex: TimePickerSegmentIndex): ITimeSegment[] {
        switch (segmentIndex) {
            case TimePickerSegmentIndex.Hours:
                return this.segments().hours;
            case TimePickerSegmentIndex.Minutes:
                return this.segments().minutes;
            case TimePickerSegmentIndex.Seconds:
                return this.segments().seconds;
        }
    }

    private getFirstEnabledSegment(segmentIndex: TimePickerSegmentIndex): ITimeSegment {
        const segment = this.getSegmentByIndex(segmentIndex);
        return (
            segment.find((segment: ITimeSegment) => !this.isTimeSegmentDisabled(segment, segmentIndex)) || segment[0]
        );
    }

    protected readonly TimePickerSegmentIndex = TimePickerSegmentIndex;
}
